// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/CheckInClaimWatcher.tsx
// 🔧 PURPOSE: L'ACCESSO AUTOMATICO dopo il check-in online. Uno solo, in
//             App.tsx: finché l'ospite non è entrato e sul telefono c'è una
//             richiesta (lib/checkInClaimStorage), chiede al channel manager
//             se il check-in è collegato alla prenotazione. Quando lo è,
//             salva la sessione nello store condiviso: tutta l'app (la Home
//             passa alla vista B o S) cambia subito, e un avviso lo dice.
//
//             Quando chiede:
//             - all'apertura dell'app;
//             - ogni 30 secondi mentre l'iframe del check-in è aperto dopo
//               la validazione (senza il flag embedded il messaggio di fine
//               non arriva: così chi finisce il check-in entra presto). Non
//               più spesso: finché il modulo non è inviato ogni risposta è
//               un "non trovato", e il channel manager li conta per
//               indirizzo IP (più telefoni sul WiFi della struttura);
//             - ogni 5 minuti con l'app in primo piano (ogni 30 per le
//               richieste di oltre un giorno), al ritorno sull'app o al
//               focus (al massimo una volta al minuto);
//             - subito alla push checkin_linked e alla chiusura dell'iframe,
//               anche durante l'attesa di un 429 (il channel manager fa
//               passare comunque un claim che conosce); se l'ultimo giro è di
//               meno di 15 secondi fa, appena passano;
//             - SUBITO, senza i 15 secondi, quando il check-in embedded
//               (nvaApp=2) dice che il modulo è inviato (CHECKIN_FORM_SUBMITTED
//               o CHECKIN_COMPLETED: requestClaimCheck urgente). Il claim a
//               quel punto il server lo conosce: niente "non trovato" da
//               contare. Se un giro è già in corso, il nuovo parte appena
//               finisce.
//             Mai più spesso di una volta ogni 15 secondi (tranne l'urgente).
//
//             Le risposte:
//             - ready: dentro; le richieste se ne vanno (saveSession);
//             - pending: si segna (la Home mostra "in attesa") e si aspetta;
//             - 404 con l'iframe ancora aperto e il modulo non inviato: "non
//               ancora inviato", si aspetta;
//             - 404: la richiesta è sparita. Se si era già in attesa (o è una
//               prenotazione diretta) si prova UNA volta il login con
//               riferimento ed email scritti nel check-in: copre la risposta
//               persa dopo il riscatto (critica 7) e, dopo lo smistamento,
//               chat/auth li accetta. Se non va: in attesa → si toglie e il
//               login la precompila ("collegamento scaduto"); mai vista dal
//               server → si tiene un giorno (il check-in può non essere
//               ancora partito), poi si toglie in silenzio;
//             - 403 booking_*: si toglie e si apre l'avviso delle
//               prenotazioni annullate;
//             - 429: si aspetta Retry-After (tranne che per gli eventi qui
//               sopra).
// ============================================

import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  authenticateGuest,
  isBookingInvalidatedCode,
  redeemCheckInClaim,
  type CheckInClaimResult,
  type GuestAuthResponse,
  type GuestBooking,
} from '../lib/guestApi';
import {
  CHECKIN_CLAIMS_EVENT,
  CHECKIN_CLAIMS_KEY,
  CHECKIN_CLAIM_CHECK_EVENT,
  UNSUBMITTED_MAX_AGE_MS,
  activeClaim,
  claimsToCheck,
  markClaimFallbackTried,
  markClaimPending,
  rememberExpiredClaim,
  removeClaim,
  type CheckInClaim,
} from '../lib/checkInClaimStorage';
import { saveSession, storedGuestToken, useGuestToken } from '../hooks/useGuestSession';
import { applyGuestLanguage } from '../lib/locale';
import { getDeviceId } from '../lib/notifications/oneSignal';
import { toast } from './ui/use-toast';

/** Con l'iframe del check-in aperto dopo la validazione (prima dell'invio sono tutti "non trovato"). */
export const FAST_POLL_MS = 30_000;
/** Con l'app in primo piano. */
export const SLOW_POLL_MS = 5 * 60_000;
/** Richieste di oltre un giorno: il check-in aspetta l'host, basta meno spesso. */
export const STALE_POLL_MS = 30 * 60_000;
/** Il minimo fra due giri, qualunque cosa li chieda. */
export const MIN_GAP_MS = 15_000;
/** Ritorno sull'app e focus: al massimo una volta al minuto. */
export const FOCUS_GAP_MS = 60_000;

// Il blocco del server (429) vale per tutto il modulo: sopravvive a un
// nuovo montaggio del watcher (uscita e rientro nella stessa sessione).
let retryUntil = 0;

/** Per i test: dimentica il blocco del server. */
export const resetClaimWatcherBackoff = (): void => {
  retryUntil = 0;
};

type RunReason = 'start' | 'timer' | 'focus' | 'event' | 'urgent';
type Outcome = 'in' | 'next' | 'stop';

export interface ClaimWatcherDeps {
  redeem: (claim: string, deviceId?: string | null) => Promise<CheckInClaimResult>;
  authenticate: (bookingReference: string, email: string, deviceId?: string | null) => Promise<GuestAuthResponse>;
  deviceId: () => Promise<string | null>;
  isLoggedIn: () => boolean;
  /** Dentro: la sessione è già salvata. */
  onReady: (booking: GuestBooking) => void;
  now: () => number;
}

export interface ClaimWatcher {
  start: () => void;
  stop: () => void;
  /** Un giro adesso (rispetta comunque il minimo di 15 secondi). */
  check: () => Promise<void>;
  /** Il modulo è appena stato inviato: un giro adesso, anche entro i 15 secondi. */
  checkNow: () => Promise<void>;
}

const invalidated = (errorCode: string): void => {
  try {
    window.dispatchEvent(new CustomEvent('nva_booking_invalidated', { detail: { errorCode } }));
  } catch {
    // niente: la richiesta è già tolta
  }
};

const isVisible = (): boolean => typeof document === 'undefined' || document.visibilityState !== 'hidden';

export function createClaimWatcher(deps: ClaimWatcherDeps): ClaimWatcher {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let running = false;
  // listening: avviato (eventi e timer); disposed: fermato per sempre.
  let listening = false;
  let disposed = false;
  let lastRun = 0;
  // Un giro urgente chiesto mentre un altro era in corso: parte appena finisce.
  let urgentQueued = false;

  const clearTimer = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  /** Fra quanto il prossimo giro; null = si aspetta un evento. */
  const nextDelay = (): number | null => {
    const now = deps.now();
    const list = claimsToCheck(now);
    if (!list.length || !isVisible()) return null;
    const active = activeClaim();
    let delay: number;
    if (active && list.some((c) => c.nonce === active)) delay = FAST_POLL_MS;
    else if (list.every((c) => now - (c.validatedAt || c.createdAt) > UNSUBMITTED_MAX_AGE_MS)) delay = STALE_POLL_MS;
    else delay = SLOW_POLL_MS;
    // Mai prima del minimo dall'ultimo giro, mai prima della fine del blocco.
    delay = Math.max(delay, lastRun + MIN_GAP_MS - now, retryUntil - now, 0);
    return delay;
  };

  const schedule = (delay: number | null = nextDelay(), reason: RunReason = 'timer') => {
    clearTimer();
    if (!listening || disposed || delay === null) return;
    timer = setTimeout(() => {
      timer = null;
      void run(reason);
    }, delay);
  };

  const signIn = (token: string, booking: GuestBooking): void => {
    saveSession(token, booking);
    applyGuestLanguage(booking);
    deps.onReady(booking);
  };

  const handleNotFound = async (claim: CheckInClaim, deviceId: string | null): Promise<Outcome> => {
    // Ancora nel modulo del check-in: il 404 vuol dire solo "non ancora
    // inviato". Non più dopo CHECKIN_FORM_SUBMITTED / COMPLETED (submittedAt):
    // allora si prova subito il login di riserva, senza aspettare la chiusura.
    if (activeClaim() === claim.nonce && !claim.submittedAt) return 'next';
    const wasPending = claim.status === 'pending';
    const canFallback = !!claim.savedBookingRef && !!claim.savedEmail && !claim.fallbackTried
      && (wasPending || claim.mode === 'normal');
    if (canFallback) {
      markClaimFallbackTried(claim.nonce);
      const res = await deps.authenticate(claim.savedBookingRef!, claim.savedEmail!, deviceId);
      if (deps.isLoggedIn()) return 'in';
      if (res.success && res.token && res.booking) {
        signIn(res.token, res.booking);
        return 'in';
      }
      if (res.errorCode && isBookingInvalidatedCode(res.errorCode)) {
        removeClaim(claim.nonce);
        invalidated(res.errorCode);
        return 'stop';
      }
    }
    if (wasPending) {
      // Era in attesa e non c'è più (scaduta, check-in cancellato): il login
      // la precompila con "collegamento scaduto".
      removeClaim(claim.nonce);
      rememberExpiredClaim(claim, deps.now());
    } else if (deps.now() - (claim.validatedAt || claim.createdAt) > UNSUBMITTED_MAX_AGE_MS) {
      removeClaim(claim.nonce);
    }
    return 'next';
  };

  const checkOne = async (claim: CheckInClaim, deviceId: string | null): Promise<Outcome> => {
    const result = await deps.redeem(claim.nonce, deviceId);
    switch (result.status) {
      case 'ready':
        // Entrato nel frattempo da un'altra strada: la richiesta è consumata.
        if (deps.isLoggedIn()) removeClaim(claim.nonce);
        else signIn(result.token, result.booking);
        return 'in';
      case 'pending':
        markClaimPending(claim.nonce, deps.now());
        return 'next';
      case 'not_found':
        return handleNotFound(claim, deviceId);
      case 'denied':
        // L'avviso globale chiude anche le altre richieste (clearSession):
        // la prenotazione di questo telefono è annullata.
        removeClaim(claim.nonce);
        invalidated(result.errorCode);
        return 'stop';
      case 'invalid':
        removeClaim(claim.nonce);
        return 'next';
      case 'rate_limited':
        retryUntil = deps.now() + result.retryAfterSec * 1000;
        return 'stop';
      default:
        // Rete o server: si riprova al prossimo giro.
        return 'stop';
    }
  };

  const run = async (reason: RunReason): Promise<void> => {
    if (disposed) return;
    if (running) {
      // Il modulo inviato non si perde dietro a un giro già partito.
      if (reason === 'urgent') urgentQueued = true;
      return;
    }
    if (deps.isLoggedIn()) {
      clearTimer();
      return;
    }
    const now = deps.now();
    const gap = reason === 'focus' ? FOCUS_GAP_MS : MIN_GAP_MS;
    // Un evento (push, iframe chiuso) non aspetta la fine del 429: il
    // channel manager blocca solo i "non trovato", un claim che conosce passa.
    const blocked = reason !== 'event' && reason !== 'urgent' && retryUntil > now;
    // Il modulo appena inviato non aspetta nemmeno i 15 secondi.
    const tooSoon = reason !== 'urgent' && now - lastRun < gap;
    if (blocked || tooSoon) {
      // L'evento troppo vicino al giro precedente si fa appena passa il
      // minimo, non al ritmo di fondo (dopo l'iframe sarebbero 5 minuti).
      if (reason === 'event') schedule(lastRun + gap - now, 'event');
      else schedule();
      return;
    }
    // Dalla più recente: di solito è quella del check-in appena fatto.
    const list = claimsToCheck(now).reverse();
    if (!list.length) {
      clearTimer();
      return;
    }
    running = true;
    lastRun = now;
    clearTimer();
    try {
      let deviceId: string | null = null;
      try {
        deviceId = await deps.deviceId();
      } catch {
        deviceId = null;
      }
      for (const claim of list) {
        if (disposed || deps.isLoggedIn()) break;
        const outcome = await checkOne(claim, deviceId);
        if (outcome !== 'next') break;
      }
    } finally {
      running = false;
      if (urgentQueued && !disposed) {
        urgentQueued = false;
        void run('urgent');
      } else {
        schedule();
      }
    }
  };

  const onVisibility = () => {
    if (isVisible()) void run('focus');
    else clearTimer();
  };
  const onFocus = () => { void run('focus'); };
  const onCheck = (e: Event) => {
    const urgent = !!(e as CustomEvent<{ urgent?: boolean } | null>).detail?.urgent;
    void run(urgent ? 'urgent' : 'event');
  };
  // Una voce nuova, validata o l'iframe chiuso: cambia il ritmo, non si chiede subito.
  const onClaimsChanged = () => {
    if (!running) schedule();
  };
  const onStorage = (e: StorageEvent) => {
    if (e.key === null || e.key === CHECKIN_CLAIMS_KEY) onClaimsChanged();
  };

  return {
    start: () => {
      if (listening || disposed) return;
      listening = true;
      document.addEventListener('visibilitychange', onVisibility);
      window.addEventListener('focus', onFocus);
      window.addEventListener(CHECKIN_CLAIM_CHECK_EVENT, onCheck);
      window.addEventListener(CHECKIN_CLAIMS_EVENT, onClaimsChanged);
      window.addEventListener('storage', onStorage);
      void run('start');
    },
    stop: () => {
      disposed = true;
      listening = false;
      clearTimer();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener(CHECKIN_CLAIM_CHECK_EVENT, onCheck);
      window.removeEventListener(CHECKIN_CLAIMS_EVENT, onClaimsChanged);
      window.removeEventListener('storage', onStorage);
    },
    check: () => run('event'),
    checkNow: () => run('urgent'),
  };
}

/**
 * Montato una volta in App.tsx. Lavora solo da fuori: appena c'è una
 * sessione (da qui o da qualunque altro accesso) si ferma.
 */
const CheckInClaimWatcher: React.FC = () => {
  const token = useGuestToken();
  const { t } = useTranslation();
  // L'avviso nella lingua di adesso (applyGuestLanguage l'ha appena cambiata).
  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    if (token) return undefined;
    const watcher = createClaimWatcher({
      redeem: redeemCheckInClaim,
      authenticate: authenticateGuest,
      deviceId: getDeviceId,
      isLoggedIn: () => !!storedGuestToken(),
      onReady: () => {
        toast({
          description: tRef.current('checkInClaim.readyToast'),
          duration: 6000,
          // Il riquadro crema col filo d'oro delle email: si stacca anche
          // sopra l'intestazione espresso della Home.
          className: 'rounded-2xl border border-nva-edge border-l-[3px] border-l-nva-gold bg-nva-card p-4 pr-10 text-[15px] font-semibold text-nva-espresso shadow-nva-lift dark:border-stone-700 dark:border-l-nva-gold dark:bg-stone-800 dark:text-stone-50',
        });
      },
      now: () => Date.now(),
    });
    watcher.start();
    return () => watcher.stop();
  }, [token]);

  return null;
};

export default CheckInClaimWatcher;
